import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useEditorContext, TEXT_ELEMENT_IDS } from '../context/EditorContext';
import type { TemplateLayer } from '../types';

// ─── Constants ────────────────────────────────────────────────

const ROW_H = 28;
const LABEL_W = 120;
const MIN_DURATION = 1; // frame

// ─── Layer type colors ────────────────────────────────────────

const TYPE_COLOR: Record<string, string> = {
  background: '#6366f1',
  anchor: '#f59e0b',
  logo: '#10b981',
  'top-bar': '#3b82f6',
  'lower-third': '#8b5cf6',
  ticker: '#ec4899',
  'breaking-news-banner': '#ef4444',
  watermark: '#64748b',
  audio: '#06b6d4',
  image: '#84cc16',
  video: '#f97316',
};

function layerColor(type: string): string {
  return TYPE_COLOR[type] ?? '#94a3b8';
}

// ─── Time ruler ───────────────────────────────────────────────

function TimeRuler({ totalFrames, fps, width }: { totalFrames: number; fps: number; width: number }) {
  const totalSec = totalFrames / fps;
  const pxPerSec = width / totalSec;
  const tickInterval = pxPerSec < 30 ? 5 : pxPerSec < 80 ? 2 : 1; // seconds between labels

  const ticks: { x: number; sec: number }[] = [];
  for (let s = 0; s <= totalSec; s += tickInterval) {
    ticks.push({ x: (s / totalSec) * width, sec: s });
  }

  return (
    <div style={{ position: 'relative', height: 20, background: '#0f172a', flexShrink: 0 }}>
      {ticks.map(({ x, sec }) => (
        <div key={sec} style={{ position: 'absolute', left: x, top: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ width: 1, height: 6, background: '#334155', marginTop: 14 }} />
          <span style={{ position: 'absolute', top: 2, left: 2, fontSize: 9, color: '#475569', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {sec}s
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Single timeline row ──────────────────────────────────────

interface TimelineRowProps {
  label: string;
  color: string;
  enterDelay: number;   // frames
  exitAt: number;       // frames (-1 = end)
  totalFrames: number;
  trackWidth: number;
  isSelected: boolean;
  onSelect: () => void;
  onTrim: (enterDelay: number, exitAt: number) => void;
}

function TimelineRow({
  label, color, enterDelay, exitAt, totalFrames, trackWidth,
  isSelected, onSelect, onTrim,
}: TimelineRowProps) {
  const effectiveExit = exitAt < 0 ? totalFrames : Math.min(exitAt, totalFrames);
  const pxPerFrame = trackWidth / totalFrames;

  const barLeft = enterDelay * pxPerFrame;
  const barRight = (totalFrames - effectiveExit) * pxPerFrame;
  const barW = Math.max(4, trackWidth - barLeft - barRight);

  const dragRef = useRef<{
    edge: 'left' | 'right';
    startX: number;
    startEnter: number;
    startExit: number;
  } | null>(null);

  const handleEdgeDrag = useCallback(
    (e: React.MouseEvent, edge: 'left' | 'right') => {
      e.stopPropagation();
      dragRef.current = { edge, startX: e.clientX, startEnter: enterDelay, startExit: effectiveExit };

      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = ev.clientX - dragRef.current.startX;
        const dFrames = Math.round(dx / pxPerFrame);
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

        if (dragRef.current.edge === 'left') {
          const newEnter = clamp(dragRef.current.startEnter + dFrames, 0, effectiveExit - MIN_DURATION);
          onTrim(newEnter, exitAt);
        } else {
          const newExit = clamp(dragRef.current.startExit + dFrames, enterDelay + MIN_DURATION, totalFrames);
          onTrim(enterDelay, newExit === totalFrames ? -1 : newExit);
        }
      };
      const onUp = () => {
        dragRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [enterDelay, effectiveExit, exitAt, pxPerFrame, onTrim, totalFrames]
  );

  return (
    <div
      onClick={onSelect}
      style={{
        height: ROW_H, display: 'flex', alignItems: 'center',
        borderBottom: '1px solid #1e293b',
        background: isSelected ? '#0f172a' : 'transparent',
        cursor: 'pointer', userSelect: 'none',
        position: 'relative',
      }}
    >
      {/* Track area */}
      <div style={{ flex: 1, height: '100%', position: 'relative', overflow: 'hidden' }}>
        {/* Bar */}
        <div
          style={{
            position: 'absolute',
            left: barLeft,
            width: barW,
            top: 5,
            bottom: 5,
            background: isSelected ? color : `${color}88`,
            borderRadius: 3,
            border: `1px solid ${color}`,
            transition: 'background 0.1s',
          }}
        >
          {/* Left trim handle */}
          <div
            onMouseDown={(e) => handleEdgeDrag(e, 'left')}
            style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: 8,
              cursor: 'w-resize', background: `${color}cc`, borderRadius: '3px 0 0 3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ width: 1, height: 10, background: '#fff8' }} />
            <div style={{ width: 1, height: 10, background: '#fff8', marginLeft: 2 }} />
          </div>
          {/* Label inside bar */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', paddingLeft: 12,
            fontSize: 9, color: '#fff', fontWeight: 600, overflow: 'hidden',
            pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            {label}
          </div>
          {/* Right trim handle */}
          <div
            onMouseDown={(e) => handleEdgeDrag(e, 'right')}
            style={{
              position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
              cursor: 'e-resize', background: `${color}cc`, borderRadius: '0 3px 3px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <div style={{ width: 1, height: 10, background: '#fff8' }} />
            <div style={{ width: 1, height: 10, background: '#fff8', marginLeft: 2 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Timeline Panel ───────────────────────────────────────────

export function Timeline({ fps = 30 }: { fps?: number }) {
  const { state, selectLayer, updateLayer, updateTextElement } = useEditorContext();
  const { editableConfig, selectedLayerId } = state;

  const [collapsed, setCollapsed] = useState(false);
  const [trackWidth, setTrackWidth] = useState(600);

  // Measure track width
  const trackRef = useRef<HTMLDivElement | null>(null);
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    trackRef.current = node;
  }, []);

  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;
    const obs = new ResizeObserver(([entry]) => setTrackWidth(entry.contentRect.width));
    obs.observe(node);
    return () => obs.disconnect();
  });

  if (!editableConfig) return null;

  const totalSec = editableConfig.metadata.duration;
  const totalFrames = totalSec * fps;

  const sortedLayers = [...editableConfig.layers].sort(
    (a, b) => a.position.zIndex - b.position.zIndex
  );

  const handleLayerTrim = (layer: TemplateLayer) => (enterDelay: number, exitAt: number) => {
    updateLayer(layer.id, {
      animation: { ...layer.animation, enterDelay, exitAt },
    });
  };

  const handleTextTrim = (id: typeof TEXT_ELEMENT_IDS.HEADLINE | typeof TEXT_ELEMENT_IDS.SUBHEADLINE) =>
    (enterDelay: number, exitAt: number) => {
      const field = id === TEXT_ELEMENT_IDS.HEADLINE ? editableConfig.headline : editableConfig.subheadline;
      updateTextElement(id, {
        animation: { ...field.animation, enterDelay, exitAt },
      });
    };

  return (
    <div
      style={{
        flexShrink: 0,
        background: '#0a0f1e',
        borderTop: '1px solid #1e293b',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: collapsed ? 32 : 200,
        transition: 'max-height 0.2s',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          height: 32, display: 'flex', alignItems: 'center',
          padding: '0 10px', cursor: 'pointer', flexShrink: 0,
          borderBottom: collapsed ? 'none' : '1px solid #1e293b',
          gap: 8,
        }}
      >
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="currentColor"
          style={{ color: '#475569', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }}
        >
          <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth={1.5} fill="none" strokeLinecap="round" />
        </svg>
        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Timeline
        </span>
        <span style={{ fontSize: 9, color: '#334155', marginLeft: 'auto' }}>
          {totalSec}s · {fps}fps · {sortedLayers.length + 2} layers
        </span>
      </div>

      {!collapsed && (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Scroll area */}
          <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Ruler */}
            <div style={{ display: 'flex' }}>
              <div style={{ width: LABEL_W, flexShrink: 0 }} />
              <div ref={measuredRef} style={{ flex: 1, overflow: 'hidden' }}>
                <TimeRuler totalFrames={totalFrames} fps={fps} width={trackWidth} />
              </div>
            </div>

            {/* Text element rows */}
            {[
              { id: TEXT_ELEMENT_IDS.HEADLINE, label: 'Headline', color: '#eab308', anim: editableConfig.headline.animation },
              { id: TEXT_ELEMENT_IDS.SUBHEADLINE, label: 'Subheadline', color: '#22d3ee', anim: editableConfig.subheadline.animation },
            ].map(({ id, label, color, anim: textAnim }) => (
              <div key={id} style={{ display: 'flex', height: ROW_H }}>
                {/* Label */}
                <div style={{
                  width: LABEL_W, flexShrink: 0,
                  display: 'flex', alignItems: 'center', paddingLeft: 8,
                  borderBottom: '1px solid #1e293b',
                  borderRight: '1px solid #1e293b',
                }}>
                  <span style={{ fontSize: 9, color, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    T {label}
                  </span>
                </div>
                {/* Track */}
                <div style={{ flex: 1 }}>
                  <TimelineRow
                    label={label}
                    color={color}
                    enterDelay={textAnim?.enterDelay ?? 0}
                    exitAt={textAnim?.exitAt ?? -1}
                    totalFrames={totalFrames}
                    trackWidth={trackWidth}
                    isSelected={selectedLayerId === id}
                    onSelect={() => selectLayer(id)}
                    onTrim={handleTextTrim(id as typeof TEXT_ELEMENT_IDS.HEADLINE)}
                  />
                </div>
              </div>
            ))}

            {/* Layer rows */}
            {sortedLayers.map((layer) => {
              const color = layerColor(layer.type);
              return (
                <div key={layer.id} style={{ display: 'flex', height: ROW_H }}>
                  {/* Label */}
                  <div style={{
                    width: LABEL_W, flexShrink: 0,
                    display: 'flex', alignItems: 'center', paddingLeft: 8, gap: 4,
                    borderBottom: '1px solid #1e293b',
                    borderRight: '1px solid #1e293b',
                  }}>
                    <div style={{ width: 6, height: 6, borderRadius: 1, background: color, flexShrink: 0 }} />
                    <span style={{ fontSize: 9, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {layer.id}
                    </span>
                  </div>
                  {/* Track */}
                  <div style={{ flex: 1 }}>
                    <TimelineRow
                      label={layer.id}
                      color={color}
                      enterDelay={layer.animation?.enterDelay ?? 0}
                      exitAt={layer.animation?.exitAt ?? -1}
                      totalFrames={totalFrames}
                      trackWidth={trackWidth}
                      isSelected={selectedLayerId === layer.id}
                      onSelect={() => selectLayer(layer.id)}
                      onTrim={handleLayerTrim(layer)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
